import type { TransformRunStatus } from "@Simplicity-Education/core";
import { useEffect, useState } from "react";
import { apiFetch } from "../../api";

interface RunStatusResponse {
  computedAt: string;
  lookbackDays: number;
  transforms: TransformRunStatus[];
}

export interface TransformRunStatusState {
  /** Keyed by TransformDefinition id. */
  byDefinition: Map<string, TransformRunStatus>;
  status: "loading" | "ready" | "unavailable";
  /** Server's reduce-time clock — ages are measured against this, not the browser's. */
  computedAtMs: number;
  lookbackDays: number;
}

const EMPTY: Map<string, TransformRunStatus> = new Map();

/**
 * SIM-2729: per-transform run facts from `GET /api/admin/run-status/:projectId`. Same endpoint as
 * the table column — one round trip serves both surfaces, since both reduce from the same plan
 * window.
 */
export function useTransformRunStatus(projectId: string | undefined): TransformRunStatusState {
  const [state, setState] = useState<TransformRunStatusState>({
    byDefinition: EMPTY,
    status: "loading",
    computedAtMs: 0,
    lookbackDays: 0,
  });

  useEffect(() => {
    if (projectId == null) return;
    let cancelled = false;
    apiFetch<RunStatusResponse>(`/api/admin/run-status/${projectId}`)
      .then((res) => {
        if (cancelled) return;
        const computedAtMs = Date.parse(res.computedAt);
        setState({
          byDefinition: new Map(res.transforms.map((t) => [t.definitionId, t])),
          status: "ready",
          computedAtMs: Number.isFinite(computedAtMs) ? computedAtMs : Date.now(),
          lookbackDays: res.lookbackDays,
        });
      })
      .catch(() => {
        // Supplementary data — must never break the page it decorates. And it
        // must resolve to "unavailable", never to an empty ready map: an absent
        // definition legitimately means "never run".
        if (cancelled) return;
        setState({
          byDefinition: EMPTY,
          status: "unavailable",
          computedAtMs: Date.now(),
          lookbackDays: 0,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return state;
}
